import React from 'react';
import { throttle } from 'lodash-es';
import millify from 'millify';
import ms from 'ms';
import { QuantityInput, quantityReducer } from '@components/main-components/inputs/QuantityInput';
import { StandardModal } from '@components/main-components/modals/StandardModal';
import { TieredBuyButtons } from '@components/modals/Buy/Checkout/TieredBuyButtons';
import { ReleaseDetails } from '@components/modals/Buy/shared';
import { ModalContext } from '@context/modal';
import {
  type FragmentType,
  getFragment,
  gql,
  TiersCheckoutReleaseInfoFragmentDoc,
} from '@graphql/reactQuery';
import { useSubscribeReleaseCollector } from '@hooks/useSubscribeRelease';
import { bigIntToNumber } from '@utils/bigNumberHelpers';
import { ADDRESS_BATCH_MINT_LIMIT, SPACE } from '@utils/constants';
import type { PlaylistReferral } from '@utils/types';
import { MINT_COMPONENT_HEADING_SUBTEXT } from '../constants';
import { TiersPriceBreakdown } from './PriceBreakdown';
import {
  hasSaleStarted,
  useHasAccessToTieredSchedule,
  useMintSchedule,
  useTiersEditionEligibleQuantity,
} from './data';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment TiersCheckoutReleaseInfo on Release {
    id
    chainId
    auctionContractType
    contractAddress
    ...ReleaseDetails
    ...TieredBuyButtons
  }
`);

export type SelectedMintPanel = 'GA_PUBLIC' | 'VIP_PUBLIC' | 'VIP_PRESALE';

export const TiersCheckoutModal = ({
  releaseData,
  selectedMintPanel,
  playlistReferral,
}: {
  releaseData: FragmentType<TiersCheckoutReleaseInfoFragmentDoc>;
  selectedMintPanel: SelectedMintPanel;
  playlistReferral: PlaylistReferral | null;
}) => {
  const release = getFragment(TiersCheckoutReleaseInfoFragmentDoc, releaseData);
  const { chainId, id: releaseId, contractAddress } = release;
  const { data, isLoading } = useMintSchedule({ editionAddress: contractAddress, chainId });
  const { closeCancelModal } = ModalContext.useContainer();

  const [state, dispatch] = React.useReducer(quantityReducer, {
    value: 1,
    error: false,
  });

  const selectedSchedule = React.useMemo(() => {
    if (!data) return null;
    if (isLoading) return null;
    switch (selectedMintPanel) {
      case 'GA_PUBLIC':
        return data.schedules.gaTier.public;
      case 'VIP_PUBLIC':
        return data.schedules.vipTier.public;
      case 'VIP_PRESALE':
        return data.schedules.vipTier.presale;
      default:
        return null;
    }
  }, [data, isLoading, selectedMintPanel]);

  const { data: hasSaleAccess = false } = useHasAccessToTieredSchedule({
    mintSchedule: selectedSchedule,
    editionAddress: contractAddress,
    chainId,
  });

  const { data: eligibleQuantity = null, refetch: refetchEligibleQuantity } =
    useTiersEditionEligibleQuantity({
      mintSchedule: selectedSchedule,
      chainId,
      editionAddress: contractAddress,
    });

  const headingText = React.useMemo(() => {
    switch (selectedMintPanel) {
      case 'GA_PUBLIC':
        return MINT_COMPONENT_HEADING_SUBTEXT.FOREVER.heading;
      case 'VIP_PUBLIC':
        return (
          <span>
            {MINT_COMPONENT_HEADING_SUBTEXT.FALLBACK.heading}
            {SPACE}•{SPACE}Max:{SPACE}
            {eligibleQuantity
              ? millify(bigIntToNumber(eligibleQuantity, { strict: false }))
              : '...'}
          </span>
        );
      case 'VIP_PRESALE':
        return (
          <span>
            {MINT_COMPONENT_HEADING_SUBTEXT.FALLBACK.heading}
            {SPACE}
            <span className="text-fuchsia500">Presale</span>
            <span className="text-base500">
              {SPACE}•{SPACE}
              Max:{SPACE}
              {eligibleQuantity
                ? millify(bigIntToNumber(eligibleQuantity, { strict: false }))
                : '...'}
            </span>
          </span>
        );
      default:
        return '';
    }
  }, [selectedMintPanel, eligibleQuantity]);

  const saleStarted = React.useMemo(() => {
    // we don't have a start time it means sale hasn't started
    if (!selectedSchedule?.startTime) return false;

    return hasSaleStarted({
      startTimestamp: selectedSchedule.startTime,
    });
  }, [selectedSchedule?.startTime]);

  const throttledRefetchEligibleQuantity = React.useMemo(
    () =>
      throttle(refetchEligibleQuantity, ms('10 seconds'), {
        leading: true,
        trailing: true,
      }),
    [refetchEligibleQuantity],
  );

  useSubscribeReleaseCollector({
    releaseId,
    onData() {
      throttledRefetchEligibleQuantity();
    },
    enabled: true,
  });

  const inputMax = eligibleQuantity
    ? Math.min(bigIntToNumber(eligibleQuantity, { strict: false }), ADDRESS_BATCH_MINT_LIMIT)
    : 1;

  if (release.auctionContractType !== 'TIERED_EDITION') {
    // TODO: throw error
    return null;
  }

  //   Handle loading states
  if (isLoading || !data?.schedules || !selectedSchedule) {
    return null;
  }

  const checkout = (
    <div className="flex flex-col gap-4">
      <ReleaseDetails
        releaseData={release}
        selectedMintPanel={selectedMintPanel === 'GA_PUBLIC' ? 'GA' : 'LIMITED'}
      />

      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <span className="text-base-m text-base800">{headingText}</span>
          <QuantityInput
            state={state}
            disabled={!saleStarted || !hasSaleAccess}
            min={1}
            dispatch={dispatch}
            max={inputMax}
          />
        </div>

        <TiersPriceBreakdown
          selectedMintPanel={selectedMintPanel}
          schedule={selectedSchedule}
          quantity={state.value || 0}
          chainId={chainId}
          editionAddress={contractAddress}
        />
      </div>
    </div>
  );

  const ctaButtons = (
    <div className="w-full">
      <TieredBuyButtons
        releaseData={release}
        schedule={selectedSchedule}
        error={state.error}
        quantity={state.value || 0}
        playlistReferral={playlistReferral}
      />
    </div>
  );

  return (
    <StandardModal
      title="Checkout"
      body={checkout}
      closeModal={closeCancelModal}
      callToAction={ctaButtons}
      bodyWithoutPadding
    />
  );
};
